<script setup lang="ts">
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import { useI18n } from 'vue-i18n'
import slugify from 'slugify'
import { saveAs } from 'file-saver'
import { faArrowDownToLine, faFileWord } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import { PendoFeature } from '~/models/Pendo'
import { useAppColor } from '~/composables/useAppColor'
import exportToWordApi from '~/api/exportToWordApi'
import Button from '~/components/ui/Button.vue'
import AuPopover from '~/components/ui/AuPopover.vue'

const {
  item,
  fields,
  currentImage,
  disabled = false,
} = defineProps<{
  item: ContentFormWriter
  fields: string[]
  currentImage?: string
  disabled?: boolean
}>()

const { t } = useI18n()
const { postWord } = exportToWordApi()
const { themeColor } = useAppColor()

async function exportToWord() {
  if (!currentImage && !fields.length) {
    return AuToast.error(t('formWriter.toasts.nothingToExport'))
  }

  try {
    const response = await postWord({
      image: currentImage ?? '',
      fields,
    })
    saveAs(new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }), `${slugify(item.title)}.docx`)
  } catch (err) {
    AuToast.error(t('formWriter.toasts.fileDownloadError'))
    throw err
  } finally {
    AuToast.success(t('formWriter.toasts.fileDownloadSuccess'))
  }
}
</script>

<template>
  <AuPopover
    side="bottom"
    align="start"
    :tooltip-content="t('formWriter.tooltips.download')"
  >
    <template #trigger>
      <Button
        shape="square"
        variant="outline"
        :disabled="disabled"
        :color="themeColor"
        size="lg"
        :aria-label="t('formWriter.tooltips.download')"
      >
        <font-awesome-icon :icon="faArrowDownToLine" />
      </Button>
    </template>
    <template #content>
      <div class="flex flex-col gap-2">
        <Button
          v-if="item.exportWord"
          variant="ghost"
          :color="themeColor"
          size="lg"
          full-width
          :disabled="disabled"
          :data-pendo="PendoFeature.FormWriter.DownloadWord"
          @click="exportToWord"
        >
          <font-awesome-icon :icon="faFileWord" />
          {{ t('formWriter.downloadWord') }}
        </Button>
      </div>
    </template>
  </AuPopover>
</template>
